/**
 * Backup / Restore API service
 *
 * Uses native fetch (not axios) for the download endpoint so the ZIP blob can
 * be piped to URL.createObjectURL for a browser-triggered file save.
 */
import api, { getErrorMessage, postMultipart } from './api'
import type { BackupExportOptions, BackupInfo, BackupPreflight, RestoreResult } from '../types'

export async function getBackupInfo(): Promise<BackupInfo> {
  try {
    const response = await api.get<BackupInfo>('/v1/backup/info')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function downloadBackup(options: BackupExportOptions): Promise<void> {
  const response = await fetch(`/api/v1/backup/export`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options),
  })

  if (!response.ok) {
    let detail = 'Export failed'
    try {
      const body = await response.json()
      detail = body.detail ?? detail
    } catch {
      // ignore
    }
    throw new Error(detail)
  }

  const blob = await response.blob()
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename = match ? match[1] : `fcc-backup-${new Date().toISOString().slice(0, 10)}.zip`

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export async function preflightRestore(file: File): Promise<BackupPreflight> {
  const formData = new FormData()
  formData.append('file', file)
  try {
    const response = await postMultipart<BackupPreflight>('/v1/backup/preflight', formData)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function restoreBackup(
  file: File,
  mode: 'merge' | 'fresh',
  passphrase?: string,
  sections?: string,
): Promise<RestoreResult> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('mode', mode)
  if (passphrase) formData.append('passphrase', passphrase)
  if (sections) formData.append('sections', sections)

  try {
    const response = await postMultipart<RestoreResult>('/v1/backup/restore', formData)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}
